import { AddressFields } from "@/components/AddressAutoComplete/utils";
import { useCallback, useEffect } from "react";
import { FieldValues as FieldValues$, Path, PathValue, useFormContext } from "react-hook-form";

const autoCompleteFields: Array<keyof Omit<AddressFields, "isVerified">> = [
  "address1",
  "address2",
  "city",
  "state",
  "postalCode",
  "countryCode"
];

type MapName<FieldValues extends FieldValues$> = (name: keyof AddressFields) => Path<FieldValues>;

type Props<FieldValues extends FieldValues$> = { mapName: MapName<FieldValues> };

export default function useAddressAutocomplete<FieldValues extends FieldValues$>({ mapName }: Props<FieldValues>) {
  const { setValue: setValue$, watch, trigger } = useFormContext<FieldValues>();

  const setValue = useCallback(
    (key: keyof AddressFields, value: string | boolean) => {
      setValue$(mapName(key), value as PathValue<FieldValues, Path<FieldValues>>);
    },
    [setValue$, mapName]
  );

  const setIsVerified = useCallback(
    (verified: boolean) => {
      setValue("isVerified", verified);
    },
    [setValue]
  );

  const onSuggest = useCallback(
    (address: AddressFields) => {
      let key: keyof AddressFields;
      for (key in address) setValue(key, address[key]);
      trigger();
    },
    [trigger, setValue]
  );

  const reset = useCallback(() => {
    let key: keyof AddressFields;
    for (key of autoCompleteFields) setValue(key, "");
    setValue("countryCode", "USA");
    setIsVerified(false);
    trigger();
  }, [setValue, setIsVerified, trigger]);

  const country = `${watch(mapName("countryCode"))}`;
  const isUSA = country === "USA";
  const isVerified = watch(mapName("isVerified"));

  useEffect(() => {
    const { unsubscribe } = watch((_, { name, type }) => {
      const isAddressField = name && autoCompleteFields.map((f) => mapName(f) as string).includes(name);
      if (isAddressField && isVerified && type) setIsVerified(false);
    });
    return () => unsubscribe();
  }, [watch, mapName, setIsVerified, isVerified]);

  const onReset = isVerified ? reset : undefined;

  return { onReset, isUSA, onSuggest, country };
}
